import { fromBase64Url, randomBytes, toBase64Url } from './bytes.js';
import { DOMAIN, domainSeparated } from './hash.js';
import { exportP256PublicJwk, generateP256KeyPair, importP256PublicJwk, isEcPublicJwk, jwkThumbprint, signP256, verifyP256, } from './keys.js';
/** Generate a software daemon key (v1 fallback). The returned JWK must go into the OS keystore. */
export async function generateSoftwareDaemonKey() {
    const pair = await generateP256KeyPair({ extractable: true });
    const jwk = (await globalThis.crypto.subtle.exportKey('jwk', pair.privateKey));
    if (typeof jwk.d !== 'string' || typeof jwk.x !== 'string' || typeof jwk.y !== 'string') {
        throw new Error('failed to export daemon key');
    }
    return { kty: 'EC', crv: 'P-256', x: jwk.x, y: jwk.y, d: jwk.d };
}
/** Load a software daemon key into a signer; the in-memory CryptoKey is non-extractable. */
export async function loadSoftwareDaemonSigner(key) {
    const privateKey = await globalThis.crypto.subtle.importKey('jwk', { kty: 'EC', crv: 'P-256', x: key.x, y: key.y, d: key.d }, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign']);
    return signerFromKey(privateKey, { kty: 'EC', crv: 'P-256', x: key.x, y: key.y }, 'software');
}
export async function signerFromKey(privateKey, publicKey, backend = 'software') {
    const publicJwk = isEcPublicJwk(publicKey)
        ? { kty: 'EC', crv: 'P-256', x: publicKey.x, y: publicKey.y }
        : await exportP256PublicJwk(publicKey);
    return {
        keyId: await jwkThumbprint(publicJwk),
        publicJwk,
        backend,
        sign: (data) => signP256(privateKey, data),
    };
}
/** Sign an arbitrary JSON statement under a domain tag. Returns base64url raw r||s. */
export async function signStatement(signer, domain, value) {
    return toBase64Url(await signer.sign(domainSeparated(domain, value)));
}
export async function verifyStatement(publicKey, domain, value, signature) {
    try {
        const key = isEcPublicJwk(publicKey) ? await importP256PublicJwk(publicKey) : publicKey;
        return await verifyP256(key, fromBase64Url(signature), domainSeparated(domain, value));
    }
    catch {
        return false;
    }
}
export const DAEMON_AUTH_MAX_AGE_MS = 60_000;
export function createDaemonAuthChallenge(now = Date.now()) {
    return { hubNonce: toBase64Url(randomBytes(32)), issuedAt: now };
}
export async function respondToDaemonAuthChallenge(signer, input) {
    const statement = {
        v: 1,
        type: 'viko.daemon-auth',
        machineId: input.machineId,
        daemonKeyId: signer.keyId,
        hubNonce: input.challenge.hubNonce,
        hubOrigin: input.hubOrigin,
        issuedAt: input.now ?? Date.now(),
    };
    return { statement, signature: await signStatement(signer, DOMAIN.daemonAuth, statement) };
}
/** Hub-side check of a daemon's proof of possession of its enrolled identity key. */
export async function verifyDaemonAuthResponse(response, expected) {
    const s = response?.statement;
    if (typeof s !== 'object' ||
        s === null ||
        s.v !== 1 ||
        s.type !== 'viko.daemon-auth' ||
        typeof response.signature !== 'string') {
        return { ok: false, reason: 'malformed' };
    }
    if (s.machineId !== expected.machineId)
        return { ok: false, reason: 'machine-mismatch' };
    if (s.daemonKeyId !== (await jwkThumbprint(expected.publicJwk))) {
        return { ok: false, reason: 'key-mismatch' };
    }
    if (s.hubNonce !== expected.challenge.hubNonce)
        return { ok: false, reason: 'nonce-mismatch' };
    if (s.hubOrigin !== expected.hubOrigin)
        return { ok: false, reason: 'origin-mismatch' };
    const now = expected.now ?? Date.now();
    const maxAge = expected.maxAgeMs ?? DAEMON_AUTH_MAX_AGE_MS;
    if (now - expected.challenge.issuedAt > maxAge)
        return { ok: false, reason: 'expired' };
    if (!(await verifyStatement(expected.publicJwk, DOMAIN.daemonAuth, s, response.signature))) {
        return { ok: false, reason: 'bad-signature' };
    }
    return { ok: true };
}
//# sourceMappingURL=daemon-identity.js.map